import {
  CalendarClock,
  Trash2,
  RotateCcw,
  Pencil,
  CircleDot,
  Calendar,
  Users,
  Tag,
  Paperclip,
  Box,
  MessageCircle,
  GitBranch,
  ScanEye,
  MapPin,
  Workflow,
  Pin,
  SquareCheck,
  type LucideIcon,
} from "lucide-react";
import type { PMActivityActionType } from "@/domains/pm/types";

/**
 * Per-action-type icon for activity rows (Notifications feed + Issue history).
 * Reuses the same lucide icons as the /issues filters/group-by so a row's kind
 * is recognisable at a glance.
 */
export const ACTIVITY_ICON: Record<PMActivityActionType, LucideIcon> = {
  issue_created: CalendarClock,
  issue_deleted: Trash2,
  issue_restored: RotateCcw,
  title_changed: Pencil,
  status_changed: CircleDot,
  start_date_changed: Calendar,
  due_date_changed: Calendar,
  parent_changed: GitBranch,
  assignee_added: Users,
  assignee_removed: Users,
  discipline_added: Tag,
  discipline_removed: Tag,
  document_attached: Paperclip,
  document_detached: Paperclip,
  element_linked: Box,
  element_unlinked: Box,
  comment_added: MessageCircle,
  comment_edited: MessageCircle,
  // Same icon the viewer's issue rows use for "Restore saved view".
  viewpoint_saved: ScanEye,
  viewpoint_cleared: ScanEye,
  drawing_anchor_saved: MapPin,
  drawing_anchor_cleared: MapPin,
  dependency_added: Workflow,
  dependency_removed: Workflow,
  schedule_pin_changed: Pin,
  checklist_item_added: SquareCheck,
  checklist_item_removed: SquareCheck,
  checklist_item_renamed: SquareCheck,
  checklist_item_checked: SquareCheck,
  checklist_item_unchecked: SquareCheck,
};

/** Filterable activity categories — each groups one or more action types.
 *  `key` maps to a label in `t.pm.notifications.filter.categories[key]`. */
export interface ActivityCategory {
  key: string;
  icon: LucideIcon;
  actionTypes: PMActivityActionType[];
}

export const ACTIVITY_CATEGORIES: ActivityCategory[] = [
  {
    key: "created",
    icon: CalendarClock,
    actionTypes: ["issue_created", "issue_deleted", "issue_restored"],
  },
  { key: "title", icon: Pencil, actionTypes: ["title_changed"] },
  { key: "status", icon: CircleDot, actionTypes: ["status_changed"] },
  // Both scheduling dates share one filter row, and so does pinning: they are
  // all "when is this happening" edits. A separate category would fragment the
  // filter for fields most issues never set.
  {
    key: "dueDate",
    icon: Calendar,
    actionTypes: ["due_date_changed", "start_date_changed", "schedule_pin_changed"],
  },
  {
    key: "dependencies",
    icon: Workflow,
    actionTypes: ["dependency_added", "dependency_removed"],
  },
  { key: "assignees", icon: Users, actionTypes: ["assignee_added", "assignee_removed"] },
  { key: "disciplines", icon: Tag, actionTypes: ["discipline_added", "discipline_removed"] },
  { key: "files", icon: Paperclip, actionTypes: ["document_attached", "document_detached"] },
  {
    key: "elements",
    icon: Box,
    // Viewpoints ride along here rather than getting their own filter: one is
    // always saved against an element, so anyone filtering for 3D-model
    // activity wants both. Without a category they would be unreachable
    // whenever any filter is applied.
    actionTypes: [
      "element_linked",
      "element_unlinked",
      "viewpoint_saved",
      "viewpoint_cleared",
      "drawing_anchor_saved",
      "drawing_anchor_cleared",
    ],
  },
  { key: "comments", icon: MessageCircle, actionTypes: ["comment_added", "comment_edited"] },
  {
    key: "checklist",
    icon: SquareCheck,
    actionTypes: [
      "checklist_item_added",
      "checklist_item_removed",
      "checklist_item_renamed",
      "checklist_item_checked",
      "checklist_item_unchecked",
    ],
  },
  { key: "parent", icon: GitBranch, actionTypes: ["parent_changed"] },
];

/** Expand a set of selected category keys to the concrete action types to
 *  query. Empty selection → undefined (no action_type filter). */
export function categoriesToActionTypes(
  categoryKeys: string[]
): PMActivityActionType[] | undefined {
  if (categoryKeys.length === 0) return undefined;
  const set = new Set(categoryKeys);
  return ACTIVITY_CATEGORIES.filter((c) => set.has(c.key)).flatMap((c) => c.actionTypes);
}
